/**
 * Tier List: a curva de dificuldade da Season 1, e as réguas de coleta e criação.
 *
 * <p>Desenha o mesmo mapa da Criação do Mundo — mesma geometria, mesmas cores
 * cadastradas —, mas responde outra pergunta. Lá o hexágono é um lugar com narrativa e
 * criaturas; aqui ele é um tier.
 *
 * <p><b>O tier não é escrito à mão.</b> As áreas vêm da API e o tier sai da posição, por
 * `tierDaArea`. Guardar os 21 números seria guardar o resultado da regra em vez da
 * regra — e no dia em que alguém movesse uma área, a tela continuaria dizendo o tier
 * antigo.
 *
 * <p><b>O que é rascunho é gravado no GG Pilot.</b> O tier de cada perícia por área e as
 * equações das duas tier lists não têm campo próprio — são guardadas como documento,
 * inteiro. Enquanto forem hipótese isso basta; quando virarem decisão, viram coluna. O
 * que mudou é que elas deixaram de morar no navegador de quem as digitou: é trabalho do
 * time, e o time precisa vê-lo.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Link } from 'react-router-dom';

import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { pontosDoHexagono } from '../mundo/geometriaDoMapa';
import * as mundoApi from '../mundo/mundoApi';
import { PericiaIcone } from '../mundo/PericiaIcone';
import type { Mapa } from '../mundo/tipos';
import { LinhaDeRolagem } from './LinhaDeRolagem';
import { linhasDoNome, semTier } from './nomeDaArea';
import { COLETAS, CRIACOES, PERICIAS, TIERS, corDe, tiersEmBranco } from './pericias';
import type { Equacao, ExigenciaPorTier, Pericia } from './pericias';
import { AvisoDeGravacao, PAGINAS, useRascunho } from './rascunhoDeGameDesign';
import { interpretarRolagens, rolagensDoNavegador, rolagensEmBranco } from './rolagens';
import type { Rolagens } from './rolagens';
import { mapaDaSeason, PERFIL_DA_REGIAO } from './tierDaArea';
import type { AreaComTier } from './tierDaArea';
import { fileiraDeIcones } from './fileiraDeIcones';
import {
  definirTeto,
  interpretarTetos,
  semT3NaRegiao,
  tiersPossiveis,
  vocacaoDa,
  TIER_DE_TODA_AREA,
} from './tierDaPericia';
import type { TetosPorArea } from './tierDaPericia';
import './areasETier.css';

/**
 * A escala do tier: uma família só, de sálvia a sangue seco.
 *
 * <p>Cinco matizes diferentes somados às três cores das regiões davam quinze cores
 * competindo — o mapa virava mostruário. Numa família só, o olho lê a escala como
 * escalada, e a cor da região continua sendo a cor da região.
 */
const COR_TIER: Record<number, string> = {
  1: '#93b089',
  2: '#bfae6a',
  3: '#d29a4a',
  4: '#c26a3d',
  5: '#b4483f',
};

const OS_TIERS = [
  { t: 1, nome: 'Chegada', frase: 'As três áreas coladas no nascimento.' },
  { t: 2, nome: 'Assentamento', frase: 'O centro de cada região.' },
  { t: 3, nome: 'Fronteira', frase: 'Onde o mundo deixa de perdoar.' },
  { t: 4, nome: 'Ermo', frase: 'Longe de casa, e sem atalho de volta.' },
  { t: 5, nome: 'Confim', frase: 'A borda do mundo. Uma por região.' },
];

const O_QUE_DEFINE = [
  { icone: 'combate', titulo: 'Inimigos', texto: 'Nível e força das criaturas da área.' },
  { icone: 'herbalismo', titulo: 'Coletáveis', texto: 'O tier do minério, da erva e do couro.' },
  { icone: 'forja', titulo: 'Quests', texto: 'A exigência e a recompensa das missões.' },
  { icone: 'alquimia', titulo: 'Drops', texto: 'O que cai do que se mata e do que se abre.' },
];

const CHAVE_MARCACOES = 'gg-pilot:areas-e-tier:pericias';

/**
 * As exigências de tier — só elas.
 *
 * <p>A chave é `:v3` porque o conteúdo encolheu: as equações saíram daqui e foram para
 * `rolagens.ts`, junto das de combate, porque eram a mesma conta guardada duas vezes. A
 * `:v2` guardava `{ equacoes, tiers }`; esta guarda só os tiers, e os números já
 * digitados são semeados da antiga uma vez.
 */
const CHAVE_TIERS = 'gg-pilot:areas-e-tier:tiers:v3';
const CHAVE_TIERS_ANTIGA = 'gg-pilot:areas-e-tier:regras:v2';

type TiersPorGrupo = Record<string, ExigenciaPorTier>;

/** As perícias marcadas em cada área, pela chave `regiaoSlot-slot`. */
type Marcacoes = Record<string, string[]>;

/** O documento da página `tier-list`: o que só esta tela escreve. */
interface Proprio {
  tiers: TiersPorGrupo;
  /** Até que tier cada perícia vai em cada área — ver `tierDaPericia.ts`. */
  tetos: TetosPorArea;
  /**
   * As marcas de antes, quando a célula era só "tem ou não tem". Ninguém mais as lê: com
   * T1 e T2 em toda área, "tem" deixou de dizer alguma coisa. Continuam no documento para
   * não sumirem do que o time gravou — e não viram T3, porque ninguém escolheu T3.
   */
  marcadas: Marcacoes;
}

const tiersDosDoisGrupos = (): TiersPorGrupo => ({
  coleta: tiersEmBranco(),
  craft: tiersEmBranco(),
});

const interpretarProprio = (guardado: unknown): Proprio => {
  const doc = (guardado ?? {}) as Record<string, unknown>;
  const tiers = tiersDosDoisGrupos();
  const lidos = (doc.tiers ?? {}) as Record<string, ExigenciaPorTier>;
  for (const grupo of Object.keys(tiers)) {
    if (lidos[grupo]) tiers[grupo] = { ...tiers[grupo], ...lidos[grupo] };
  }
  const marcadas =
    doc.marcadas && typeof doc.marcadas === 'object' ? (doc.marcadas as Marcacoes) : {};
  return { tiers, tetos: interpretarTetos(doc.tetos), marcadas };
};

/**
 * O rascunho que ficou no navegador, para a migração de uma vez só.
 *
 * <p>Semeia também da `:v2`, que guardava `{ equacoes, tiers }` — as equações saíram
 * dali e foram para `rolagens.ts`, e os tiers já digitados continuam valendo.
 */
function semearProprio(): Proprio | undefined {
  const cru = (chave: string) => {
    try {
      return localStorage.getItem(chave);
    } catch {
      return null;
    }
  };
  const novo = cru(CHAVE_TIERS);
  const antigo = cru(CHAVE_TIERS_ANTIGA);
  const marcas = cru(CHAVE_MARCACOES);
  if (novo == null && antigo == null && marcas == null) return undefined;

  const tiers = tiersDosDoisGrupos();
  try {
    if (novo) Object.assign(tiers, JSON.parse(novo));
    else if (antigo) {
      const lido = JSON.parse(antigo) as Record<string, { tiers?: ExigenciaPorTier }>;
      for (const grupo of Object.keys(tiers)) {
        if (lido[grupo]?.tiers) tiers[grupo] = { ...tiers[grupo], ...lido[grupo].tiers };
      }
    }
  } catch {
    // Rascunho ilegível vira rascunho em branco — pior seria a tela não abrir.
  }

  let marcadas: Marcacoes = {};
  try {
    if (marcas) marcadas = JSON.parse(marcas) as Marcacoes;
  } catch {
    marcadas = {};
  }

  return { tiers, tetos: {}, marcadas };
}

/**
 * Lava a cor cadastrada antes de virar terreno.
 *
 * <p>As cores do mapa da Criação do Mundo são vivas porque lá servem para separar
 * região num mapa de trabalho. Aqui elas dividem a tela com os selos de tier e os
 * ícones de perícia — vivas, brigavam com os dois.
 */
function lavar(hex: string, sat = 0.72, luz = 0.86): string {
  const c = hex.replace('#', '');
  if (c.length !== 6) return hex;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(c.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  const s2 = s * sat;
  const l2 = Math.min(1, l * luz);
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s2 * Math.min(l2, 1 - l2);
    const v = l2 - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * v).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

const chaveDa = (a: AreaComTier) => `${a.regiaoSlot}-${a.slot}`;

export function AreasETier() {
  const navegar = useNavigate();
  const [filtro, setFiltro] = useState<number | null>(null);
  /** Qual área está sob o cursor — é ela que acende o contorno da camada de cima. */
  const [sobre, setSobre] = useState<string | null>(null);

  const proprio = useRascunho(PAGINAS.tierList, interpretarProprio, semearProprio);

  /*
   * As rolagens são as MESMAS que a Personagem edita — um documento só, lido e escrito
   * na mesma página. Editar aqui muda lá, e é essa a intenção: a conta de Mineração não
   * pode ser uma coisa nesta tela e outra na ficha.
   */
  const doc = useRascunho(PAGINAS.rolagens, interpretarRolagens, rolagensDoNavegador);

  const tetos = proprio.valor?.tetos ?? {};
  const tiers = proprio.valor?.tiers ?? tiersDosDoisGrupos();
  const rolagens = doc.valor ?? rolagensEmBranco();

  const setTetos = (muda: (antes: TetosPorArea) => TetosPorArea) =>
    proprio.definir((p) => ({ ...p, tetos: muda(p.tetos) }));
  const setTiers = (muda: (antes: TiersPorGrupo) => TiersPorGrupo) =>
    proprio.definir((p) => ({ ...p, tiers: muda(p.tiers) }));
  const setRolagens = doc.definir;

  /* Um aviso só para os dois documentos — ver a mesma junção na Personagem. */
  const gravacao = {
    gravando: proprio.estado.gravando || doc.estado.gravando,
    erroAoGravar: proprio.estado.erroAoGravar ?? doc.estado.erroAoGravar,
    atualizadoPor: proprio.estado.atualizadoPor ?? doc.estado.atualizadoPor,
  };
  const erroAoLer = proprio.erroAoLer ?? doc.erroAoLer;

  const mudarRolagem = (pericia: string, campo: keyof Equacao, valor: string) =>
    setRolagens((atual) => ({ ...atual, [pericia]: { ...atual[pericia], [campo]: valor } }));

  const { data: mapa, isLoading, error } = useQuery<Mapa>({
    queryKey: ['mundo', 'mapa'],
    queryFn: mundoApi.carregarMapa,
    staleTime: Infinity,
  });

  const season = useMemo(() => (mapa ? mapaDaSeason(mapa.regioes) : null), [mapa]);

  const caixa = useMemo(() => {
    if (!season?.areas.length) return null;
    const xs = season.areas.map((a) => a.x);
    const ys = season.areas.map((a) => a.y);
    const folga = 60;
    const minX = Math.min(...xs) - 129.9 - folga;
    const minY = Math.min(...ys) - 150 - folga;
    const maxX = Math.max(...xs) + 129.9 + folga;
    const maxY = Math.max(...ys) + 150 + folga;
    return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;
  }, [season]);

  if (isLoading) return <div className="mu-nada">Carregando o mapa…</div>;
  if (error || !season || !caixa) {
    return (
      <div className="mu-nada erro">
        {error instanceof Error ? error.message : 'Não consegui carregar o mapa.'}
      </div>
    );
  }

  const contagem = (t: number) => season.areas.filter((a) => a.tier === t).length;
  const apagada = (a: AreaComTier) => filtro !== null && a.tier !== filtro;
  const tetoDe = (a: AreaComTier, id: string) => tetos[chaveDa(a)]?.[id];

  const mudarTeto = (a: AreaComTier, id: string, tier: number | null) =>
    setTetos((atual) => definirTeto(atual, chaveDa(a), id, tier));

  const totalMarcado = Object.values(tetos).flatMap((porPericia) => Object.keys(porPericia)).length;

  return (
    <div className="gd">
      <TituloDaPagina>
        A dificuldade de uma área é o <b>tier</b> dela — uma etiqueta só, que vale para
        tudo que existe lá dentro. Ela cresce com a distância até onde o jogador nasce.
      </TituloDaPagina>

      {erroAoLer ? (
        <p className="gd-gravacao erro">
          {erroAoLer instanceof Error ? erroAoLer.message : 'Não foi possível abrir.'}
        </p>
      ) : (
        <AvisoDeGravacao estado={gravacao} />
      )}

      {/* ------------------------------------------------------------ os 5 tiers */}
      <section className="gd-secao">
        <h2>Os cinco tiers</h2>
        <div className="gd-escada">
          {OS_TIERS.map(({ t, nome, frase }) => (
            <div key={t} className="gd-degrau" style={{ '--c': COR_TIER[t] } as React.CSSProperties}>
              <span className="gd-degrau-t">T{t}</span>
              <b>{nome}</b>
              <span className="gd-degrau-f">{frase}</span>
              <span className="gd-degrau-n">{contagem(t)} áreas</span>
            </div>
          ))}
        </div>
        <p className="gd-p">
          As três regiões têm o mesmo perfil — <b>{PERFIL_DA_REGIAO.map((t) => `T${t}`).join(' · ')}</b>
          . Nenhuma é mais dura que as outras: o que muda é o bioma, a criatura e o
          recurso, nunca a curva. É isso que permite balancear uma região e copiar o
          resultado para as outras duas.
        </p>
      </section>

      {/* ------------------------------------------------------ o que o tier define */}
      <section className="gd-secao">
        <h2>O que o tier define</h2>
        <div className="gd-define">
          {O_QUE_DEFINE.map((d) => (
            <div key={d.titulo}>
              <PericiaIcone pericia={d.icone} cor="var(--gold)" tamanho={22} />
              <b>{d.titulo}</b>
              <span>{d.texto}</span>
            </div>
          ))}
        </div>
        <p className="gd-p">
          Ninguém aprende duas escalas: <b>ver T4 no mapa é saber tudo</b> sobre o que vem
          pela frente.
        </p>
      </section>

      {/* --------------------------------------------------- nascimento e caminhos */}
      <section className="gd-secao">
        <h2>O nascimento e os três caminhos</h2>
        <p className="gd-p">
          O jogador nasce na <b>costura das três regiões</b>, não dentro de nenhuma. Ali
          escolhe por qual começar — e como as três portas são T1, a escolha é de
          vocação, não de risco.
        </p>
        <div className="gd-caminhos">
          {season.regioes.map((r) => {
            const vocacao = vocacaoDa(r.slot);
            const porta = season.areas.find((a) => a.regiaoSlot === r.slot && a.tier === 1);
            return (
              <div key={r.slot} className="gd-caminho" style={{ '--c': lavar(r.cor) } as React.CSSProperties}>
                <div className="gd-caminho-topo">
                  <span className="gd-caminho-reg">{r.nome}</span>
                  {vocacao && <PericiaIcone pericia={vocacao.id} cor={vocacao.cor} tamanho={28} />}
                </div>
                {vocacao && (
                  <div className="gd-caminho-voc" style={{ color: vocacao.cor }}>
                    {vocacao.rotulo}
                  </div>
                )}
                <div className="gd-caminho-porta">
                  entra por <b>{semTier(porta?.nome ?? '—')}</b>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ------------------------------------------------------------------- o mapa */}
      <section className="gd-secao">
        <h2>O mapa</h2>

        <div className="gd-filtros" role="group" aria-label="Filtrar por tier">
          <button
            className={`gd-fbt${filtro === null ? ' on' : ''}`}
            onClick={() => setFiltro(null)}
          >
            Todas <b>{season.areas.length}</b>
          </button>
          {TIERS.map((t) => (
            <button
              key={t}
              className={`gd-fbt${filtro === t ? ' on' : ''}`}
              style={{ '--c': COR_TIER[t] } as React.CSSProperties}
              onClick={() => setFiltro(filtro === t ? null : t)}
            >
              T{t} <b>{contagem(t)}</b>
            </button>
          ))}
        </div>

        <div className="gd-mapa">
          <svg viewBox={caixa} role="img" aria-label="Mapa da Season 1 com o tier de cada área">
            <defs>
              {season.regioes.map((r) => (
                <linearGradient key={r.slot} id={`gd-g${r.slot}`} x1="0.15" y1="0" x2="0.6" y2="1">
                  <stop offset="0%" stopColor={lavar(r.cor, 0.72, 1.08)} />
                  <stop offset="52%" stopColor={lavar(r.cor)} />
                  <stop offset="100%" stopColor={lavar(r.cor, 0.68, 0.64)} />
                </linearGradient>
              ))}
              <radialGradient id="gd-lavado" cx="50%" cy="46%" r="62%">
                <stop offset="62%" stopColor="#000" stopOpacity="0" />
                <stop offset="100%" stopColor="#000" stopOpacity="0.34" />
              </radialGradient>
            </defs>

            {season.areas.map((a) => {
              const linhas = linhasDoNome(semTier(a.nome));
              /* Só o que passa do T2: o T1 e o T2 estão em toda área, e não diriam nada. */
              const marcas = PERICIAS.filter((p) => tetoDe(a, p.id));
              const fileira = fileiraDeIcones(marcas.length);
              /* Duas fileiras de ícone sobem o nome para o bloco não encostar nelas. */
              const centroNome = a.y + (fileira.duasFileiras ? -8 : 1);
              const topoNome = centroNome - ((linhas.length - 1) * 34) / 2;
              return (
                <g
                  key={chaveDa(a)}
                  className={`gd-area${apagada(a) ? ' off' : ''}`}
                  onClick={() => a.areaId && navegar(`/mundo/areas/${a.areaId}`)}
                  onMouseEnter={() => setSobre(chaveDa(a))}
                  onMouseLeave={() => setSobre(null)}
                >
                  <title>
                    {[
                      `${semTier(a.nome)} · ${a.regiao} · tier ${a.tier}`,
                      ...marcas.map((p) => `${p.rotulo} até T${tetoDe(a, p.id)}`),
                    ].join('\n')}
                  </title>
                  <polygon
                    points={pontosDoHexagono(a.x, a.y)}
                    fill={`url(#gd-g${a.regiaoSlot})`}
                    stroke="#0b0f0c"
                    strokeWidth={6}
                    strokeLinejoin="round"
                  />
                  <polygon
                    points={pontosDoHexagono(a.x, a.y, 147)}
                    fill="url(#gd-lavado)"
                    pointerEvents="none"
                  />

                  <polygon
                    points={pontosDoHexagono(a.x, a.y - 90, 36)}
                    fill="#0d1210"
                    fillOpacity={0.92}
                    stroke={COR_TIER[a.tier]}
                    strokeWidth={3}
                    strokeLinejoin="round"
                    pointerEvents="none"
                  />
                  <text
                    x={a.x}
                    y={a.y - 79}
                    textAnchor="middle"
                    className="gd-tier"
                    fill={COR_TIER[a.tier]}
                    pointerEvents="none"
                  >
                    T{a.tier}
                  </text>

                  {linhas.map((linha, i) => (
                    <text
                      key={i}
                      x={a.x}
                      y={topoNome + i * 34}
                      textAnchor="middle"
                      className="gd-nome"
                      pointerEvents="none"
                    >
                      {linha}
                    </text>
                  ))}

                  {/*
                    As perícias que passam do T2, com o teto no canto do ícone. O
                    `drop-shadow` é o mesmo recurso do Mapa da Ilha: traço fino sobre
                    terreno some, e a sombra devolve a borda sem mudar a cor do ícone.
                  */}
                  {marcas.length > 0 && (
                    <g className="gd-coletas" pointerEvents="none">
                      {marcas.map((p, i) => {
                        const pos = fileira.posicao(i);
                        const teto = tetoDe(a, p.id) as number;
                        return (
                          <g key={p.id} transform={`translate(${a.x + pos.dx}, ${a.y + pos.dy})`}>
                            <PericiaIcone
                              pericia={p.id}
                              cor={corDe(p)}
                              tamanho={fileira.tamanho}
                              titulo={`${p.rotulo} até T${teto}`}
                            />
                            <text
                              x={fileira.tamanho + 1}
                              y={fileira.tamanho + 2}
                              textAnchor="middle"
                              className="gd-teto"
                              fill={COR_TIER[teto]}
                              style={{ fontSize: Math.round(fileira.tamanho * 0.62) }}
                            >
                              {teto}
                            </text>
                          </g>
                        );
                      })}
                    </g>
                  )}
                </g>
              );
            })}

            {/*
              Os contornos do hover moram numa camada ACIMA de todos os hexágonos.
              Dentro do grupo da área, a borda de quem foi desenhado antes sumia sob o
              vizinho — os hexágonos se pintam por cima uns dos outros.
            */}
            {season.areas.map((a) => (
              <polygon
                key={`c-${chaveDa(a)}`}
                className={`gd-contorno${sobre === chaveDa(a) ? ' on' : ''}${apagada(a) ? ' off' : ''}`}
                points={pontosDoHexagono(a.x, a.y, 147)}
              />
            ))}

            <g className="gd-inicio" pointerEvents="none">
              <circle cx={season.nascimento.x} cy={season.nascimento.y} r={43} fill="#0a0e0c" stroke="#c9a25a" strokeWidth={3} />
              <circle cx={season.nascimento.x} cy={season.nascimento.y} r={36} fill="none" stroke="#c9a25a" strokeOpacity={0.4} strokeWidth={1} />
              <text x={season.nascimento.x} y={season.nascimento.y + 6} textAnchor="middle" className="gd-inicio-txt">
                INÍCIO
              </text>
            </g>
          </svg>
        </div>

        <div className="gd-legenda">
          <div className="gd-leg-grupo">
            <span className="gd-leg-tit">Regiões</span>
            {season.regioes.map((r) => (
              <span key={r.slot} className="gd-leg-item">
                <i className="gd-leg-cor" style={{ background: lavar(r.cor) }} />
                {r.nome}
              </span>
            ))}
          </div>

          <div className="gd-leg-grupo">
            <span className="gd-leg-tit">Coleta</span>
            {COLETAS.map((p) => (
              <span key={p.id} className="gd-leg-item">
                <PericiaIcone pericia={p.id} cor={corDe(p)} tamanho={17} />
                {p.rotulo}
              </span>
            ))}
          </div>

          <div className="gd-leg-grupo">
            <span className="gd-leg-tit">Teto da perícia</span>
            {[3, 4, 5].map((t) => (
              <span key={t} className="gd-leg-item">
                <b className="gd-leg-teto" style={{ color: COR_TIER[t] }}>{t}</b>
                até T{t}
              </span>
            ))}
          </div>

          <div className="gd-leg-grupo">
            <span className="gd-leg-tit">Criação</span>
            {CRIACOES.map((p) => (
              <span key={p.id} className="gd-leg-item">
                <PericiaIcone pericia={p.id} cor={corDe(p)} tamanho={17} />
                {p.rotulo}
              </span>
            ))}
          </div>
        </div>

        <p className="gd-nota">
          Passe o mouse para acender o contorno; clique para abrir a ficha da área. O
          <b> INÍCIO</b> é o vértice onde as três regiões se tocam. O número no canto do
          ícone é até que tier a perícia vai ali.
        </p>
      </section>

      {/* --------------------------------------------------------------- as áreas */}
      <section className="gd-secao">
        <h2>As áreas</h2>
        <p className="gd-nota">
          Toda área tem <b>T1 e T2 de todas as perícias</b> — por isso a célula começa em
          T{TIER_DE_TODA_AREA}. O que se escolhe é onde a perícia vai além: o <b>T3</b> existe
          em todas as regiões, em algumas áreas de cada; o <b>T4 e o T5</b> só na região dona
          da perícia. O que passa do T2 aparece no hexágono, no mapa acima.
          {totalMarcado > 0 && <> <b>{totalMarcado} escolha{totalMarcado === 1 ? '' : 's'}.</b></>}{' '}
          É gravado no GG Pilot, e o time inteiro vê o mesmo.
        </p>
        <ul className="gd-donas">
          {season.regioes.map((r) => (
            <li key={r.slot}>
              <b>{r.nome}</b>: T4 e T5 de{' '}
              {PERICIAS.filter((p) => tiersPossiveis(p, r.slot).includes(5))
                .map((p) => p.rotulo)
                .join(' e ')}
            </li>
          ))}
        </ul>

        {season.regioes.map((r) => {
          const faltam = semT3NaRegiao(tetos, r.slot);
          return (
          <div key={r.slot} className="gd-quadro" style={{ '--c': lavar(r.cor) } as React.CSSProperties}>
            <h3>{r.nome}</h3>
            {/*
              A regra cobra T3 de todas as perícias em toda região. A tela aponta o que
              falta em vez de recusar: o time preenche área por área, e recusar travaria
              a primeira escolha.
            */}
            <p className={`gd-falta${faltam.length ? '' : ' ok'}`}>
              {faltam.length
                ? `Ainda sem T3 nesta região: ${faltam.map((p) => p.rotulo).join(', ')}.`
                : 'Todas as perícias chegam ao T3 nesta região.'}
            </p>
            <div className="gd-rolagem">
              <table>
                <thead>
                  <tr className="gd-grupos">
                    <th colSpan={2} />
                    <th colSpan={3} className="gd-grupo-coleta">Coleta</th>
                    <th colSpan={3} className="gd-grupo-craft">Criação</th>
                    <th />
                  </tr>
                  <tr>
                    <th className="gd-th-tier">Tier</th>
                    <th>Área</th>
                    {PERICIAS.map((p) => (
                      <th
                        key={p.id}
                        className={`gd-th-pericia${p.depoisDaSeason1 ? ' futura' : ''}`}
                        title={p.depoisDaSeason1 ? 'Só depois da Season 1' : undefined}
                      >
                        <PericiaIcone pericia={p.id} cor={corDe(p)} tamanho={17} />
                        <span>{p.rotulo}</span>
                      </th>
                    ))}
                    <th className="gd-sobra" />
                  </tr>
                </thead>
                <tbody>
                  {season.areas
                    .filter((a) => a.regiaoSlot === r.slot)
                    .sort((a, b) => a.tier - b.tier || a.distancia - b.distancia)
                    .map((a) => (
                      <tr key={a.slot}>
                        <td>
                          <span className="gd-selo" style={{ '--c': COR_TIER[a.tier] } as React.CSSProperties}>
                            T{a.tier}
                          </span>
                        </td>
                        <td>
                          <button
                            className="gd-abrir"
                            onClick={() => a.areaId && navegar(`/mundo/areas/${a.areaId}`)}
                            disabled={!a.areaId}
                          >
                            {semTier(a.nome)}
                          </button>
                        </td>
                        {PERICIAS.map((p) => {
                          const possiveis = tiersPossiveis(p, r.slot);
                          const teto = tetoDe(a, p.id);
                          return (
                            <td key={p.id} className="gd-td-pericia">
                              {/*
                                Só os tiers que a região aceita entram no seletor: T4 e T5
                                fora da região dona não aparecem, em vez de aparecerem e
                                serem recusados. A perícia que só entra depois da Season 1
                                fica sem escolha nenhuma — marcá-la gravaria um dado que
                                contradiz o próprio recorte da tela.
                              */}
                              <select
                                className={`gd-teto-sel${teto ? ' on' : ''}${p.depoisDaSeason1 ? ' futura' : ''}`}
                                style={{ '--c': teto ? COR_TIER[teto] : corDe(p) } as React.CSSProperties}
                                value={teto ?? ''}
                                onChange={(e) =>
                                  mudarTeto(a, p.id, e.target.value ? Number(e.target.value) : null)
                                }
                                disabled={!possiveis.length}
                                title={p.depoisDaSeason1 ? 'Só depois da Season 1' : undefined}
                                aria-label={`Até que tier ${p.rotulo} vai em ${semTier(a.nome)}`}
                              >
                                <option value="">T{TIER_DE_TODA_AREA}</option>
                                {possiveis.map((t) => (
                                  <option key={t} value={t}>
                                    T{t}
                                  </option>
                                ))}
                              </select>
                            </td>
                          );
                        })}
                        <td className="gd-sobra" />
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          );
        })}
      </section>

      {/* ---------------------------------------------- as duas tier lists de regra */}
      <TierListDePericia
        titulo="Tier list de coletas"
        pericias={COLETAS}
        acaoCurta="coletar"
        intro={
          <>
            Colher não é automático: o nó está lá, e o personagem <b>tenta</b>. A tentativa
            é uma rolagem, e o tier do coletável é a dificuldade dela — é assim que o T5 de
            um polo continua fora de alcance de quem acabou de pegar a picareta, sem
            precisar de nenhuma trava explícita.
          </>
        }
        rolagens={rolagens}
        aoMudarRolagem={mudarRolagem}
        tiers={tiers.coleta}
        aoMudarTier={(t, v) =>
          setTiers((atual) => ({ ...atual, coleta: { ...atual.coleta, [t]: v } }))
        }
      />

      <TierListDePericia
        titulo="Tier list de criação"
        pericias={CRIACOES}
        acaoCurta="criar"
        intro={
          <>
            Criar usa a mesma mecânica da coleta, sobre outra matéria-prima: a receita pede
            os ingredientes, e a rolagem decide se a peça sai. Como o tier da receita é o do
            seu ingrediente mais alto, <b>a régua da criação herda a da coleta</b> — quem não
            colhe o material também não forja a peça.
          </>
        }
        rolagens={rolagens}
        aoMudarRolagem={mudarRolagem}
        tiers={tiers.craft}
        aoMudarTier={(t, v) =>
          setTiers((atual) => ({ ...atual, craft: { ...atual.craft, [t]: v } }))
        }
      />
    </div>
  );
}

/**
 * Uma tier list de perícia: a rolagem, e quanto cada tier exige.
 *
 * <p>Tudo aqui é editável e nada tem campo no GG Pilot. É a mesa de trabalho para o
 * time fechar os números antes de eles virarem regra — por isso os campos nascem vazios
 * em vez de trazerem um chute pré-preenchido, que seria lido como decisão tomada.
 *
 * <p>A rolagem é a <b>mesma</b> que a Personagem edita, e por isso chega de fora em vez
 * de nascer aqui: uma cópia local voltaria a divergir da ficha na primeira edição.
 */
function TierListDePericia({
  titulo,
  intro,
  pericias,
  acaoCurta,
  rolagens,
  aoMudarRolagem,
  tiers,
  aoMudarTier,
}: {
  titulo: string;
  intro: React.ReactNode;
  pericias: Pericia[];
  acaoCurta: string;
  rolagens: Rolagens;
  aoMudarRolagem: (pericia: string, campo: keyof Equacao, valor: string) => void;
  tiers: ExigenciaPorTier;
  aoMudarTier: (t: number, valor: string) => void;
}) {
  return (
    <section className="gd-secao">
      <h2>{titulo}</h2>

      <div className="gd-pericias-do-grupo">
        {pericias.map((p) => (
          <span
            key={p.id}
            className={`gd-chip${p.depoisDaSeason1 ? ' futura' : ''}`}
            style={{ '--c': corDe(p) } as React.CSSProperties}
          >
            <PericiaIcone pericia={p.id} cor={corDe(p)} tamanho={17} />
            {p.rotulo}
          </span>
        ))}
      </div>

      <p className="gd-p">{intro}</p>

      {/*
        Uma rolagem por perícia, e não uma pelo grupo: minerar é força e vigor,
        herbalismo é percepção e mente. Uma fórmula só apagaria justamente o que
        distingue as três.
      */}
      <h3 className="gd-h3">A rolagem de cada perícia</h3>
      <div className="rl-lista">
        {pericias.map((p) => (
          <LinhaDeRolagem
            key={p.id}
            pericia={p}
            equacao={rolagens[p.id]}
            aoMudar={(campo, valor) => aoMudarRolagem(p.id, campo, valor)}
          />
        ))}
      </div>
      <p className="gd-nota">
        Estas são <b>as mesmas rolagens</b> de <Link to="/game-design/ficha">Personagem</Link>
        , e não uma cópia: editar aqui muda lá. A equação guarda a sigla do atributo, não
        o nome — renomear "Vitalidade" na ficha não pode invalidar uma fórmula montada
        aqui.
      </p>

      {/* --------------------------------------------------------- a tabela de tier */}
      <h3 className="gd-h3">Quanto cada tier exige</h3>
      <div className="gd-rolagem">
        <table className="gd-tabela-tier">
          <thead>
            <tr>
              <th className="gd-th-tier">Tier</th>
              <th>Resultado necessário</th>
              <th className="gd-sobra" />
            </tr>
          </thead>
          <tbody>
            {TIERS.map((t) => (
              <tr key={t}>
                <td>
                  <span className="gd-selo" style={{ '--c': COR_TIER[t] } as React.CSSProperties}>
                    T{t}
                  </span>
                </td>
                <td>
                  <input
                    className="gd-eq-campo gd-eq-num"
                    inputMode="numeric"
                    placeholder="—"
                    value={tiers[t] ?? ''}
                    onChange={(e) => aoMudarTier(t, e.target.value)}
                    aria-label={`Resultado para ${acaoCurta} tier ${t}`}
                  />
                </td>
                <td className="gd-sobra" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="gd-nota">
        Rascunho do time, gravado no GG Pilot. Quando os números fecharem, viram campo
        com nome próprio em vez de documento solto.
      </p>
    </section>
  );
}
